import React, { useCallback, useState } from 'react'
import { Tooltip } from 'src/components/Reusable/Tooltip/Tooltip'
import { SimpleIconButton } from '../SimpleIconButton/SimpleIconButton'
import { StyledSVG } from './TooltipIconButton.styles'

export type TooltipIconButtonProps = {
  icon: React.ReactNode
  onClick?: (e: React.MouseEvent<HTMLButtonElement>) => void
  placement?: 'top' | 'bottom' | 'left' | 'right'
  successIcon?: React.ReactNode
  tooltipText?: string
  tooltipClickedText?: string
  preserveIconStyles?: boolean
  disabled?: boolean
  light?: boolean
}

export const TooltipIconButton = ({
  icon,
  onClick,
  placement = 'top',
  successIcon,
  tooltipText,
  tooltipClickedText,
  preserveIconStyles = false,
  disabled = false,
  light = false,
}: TooltipIconButtonProps): JSX.Element => {
  const [showClickedStatus, setShowClickedStatus] = useState(false)
  const [showTooltip, setShowTooltip] = useState(false)

  const id = Math.random() * 1000000

  const onButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.preventDefault()
      onClick?.(e)
      setShowClickedStatus(!!tooltipClickedText)
      setShowTooltip(true)
      const timeout = setTimeout(() => {
        setShowClickedStatus(false)
        setShowTooltip(false)
      }, 1000)
      return () => clearTimeout(timeout)
    },
    [onClick, tooltipClickedText],
  )

  return (
    <Tooltip
      id={`tooltip-icon-button-${id}`}
      trigger={['hover', 'click']}
      tooltipBody={(showClickedStatus && tooltipClickedText) || tooltipText}
      onToggle={(nextShow) => {
        setShowTooltip(nextShow)
      }}
      show={showTooltip}
      placement={placement}
      disabled={disabled}
    >
      <SimpleIconButton
        onClick={onButtonClick}
        $preserveIconStyles={preserveIconStyles}
        $light={light}
      >
        <StyledSVG
          $visible={!showClickedStatus || !successIcon}
          $preserveIconStyles={preserveIconStyles}
        >
          {icon}
        </StyledSVG>
        <StyledSVG $visible={showClickedStatus && !!successIcon}>
          {successIcon}
        </StyledSVG>
      </SimpleIconButton>
    </Tooltip>
  )
}
